import { rtcEvents } from "../protocol/events/rtc-events";
import { ArtichokeApi } from "../artichoke/artichoke-api";
import type { ID, TurnConfig } from "../protocol/protocol";
import {
  ConnectionStatus,
  RTCPeerConnectionFacade,
} from "./rtc-peer-connection-facade";
import { Observable, Subject } from "rxjs";
import { filter } from "rxjs/operators";
import { LoggerFactory } from "../logger/logger-factory";
import { LoggerService } from "../logger/logger-service";
import { WebRTCStats } from "./stats/webrtc-stats";
import { Queue } from "../utils/queue";
import { Delayer } from "../utils/delayer";

export interface RemoteTrack {
  readonly peerId: ID;
  readonly track: MediaStreamTrack;
}

export interface PeerConnectionStatus {
  readonly peerId: ID;
  readonly status: ConnectionStatus;
}

export class RTCPool {
  private readonly peerConnections: Map<ID, RTCPeerConnectionFacade> =
    new Map();
  private readonly pendingPeerConnections: Map<ID, Promise<RTCPeerConnectionFacade>> =
    new Map();
  // tslint:disable-next-line:readonly-keyword
  private tracks: ReadonlyArray<MediaStreamTrack> = [];
  private readonly remoteTrackEvent = new Subject<RemoteTrack>();
  private readonly connectionStatusEvent = new Subject<PeerConnectionStatus>();

  private readonly logger: LoggerService;
  // Cache TURN config to avoid multiple API calls
  private turnConfigPromise: Promise<TurnConfig> | null = null;

  constructor(
    public readonly callId: ID,
    private rtcConfig: RTCConfiguration,
    private loggerFactory: LoggerFactory,
    private artichokeApi: ArtichokeApi,
    private webrtcStats: WebRTCStats,
    private recordingEnabled: boolean = false
  ) {
    this.logger = loggerFactory.create(`RTCPool(${callId})`);

    // FIXME - unsubscribe
    this.descriptionSent$.subscribe(this.listenForDescriptionSent);
    // FIXME - unsubscribe
    this.candidateSent$.subscribe(this.listenForCandidateSent);
  }

  public get peerStatus$(): Observable<PeerConnectionStatus> {
    return this.connectionStatusEvent;
  }

  public get remoteTrack$(): Observable<RemoteTrack> {
    return this.remoteTrackEvent;
  }

  public addTrack(track: MediaStreamTrack): void {
    this.tracks = [...this.tracks, track];
    this.peerConnections.forEach((peerConnection) =>
      peerConnection.addTrack(track)
    );
  }

  public removeTrack(track: MediaStreamTrack): void {
    this.tracks = this.tracks.filter((t) => t !== track);
    this.peerConnections.forEach((peerConnection) =>
      peerConnection.removeTrack(track)
    );
  }

  public async connect(peerId: ID): Promise<void> {
    return (await this.getRTCPeerConnectionInstance(peerId)).offer();
  }

  public destroyConnection(peerId: ID): void {
    const maybePeerConnection = this.peerConnections.get(peerId);
    if (maybePeerConnection) {
      this.logger.debug(`Destroying connection for peerId ${peerId}`);
      maybePeerConnection.disconnect();
      this.peerConnections.delete(peerId);
      this.pendingPeerConnections.delete(peerId);
      this.logActivePeerConnections(`destroy(${peerId})`);
    } else {
      this.logger.warn(
        `Cannot destroy connection for peerId ${peerId} - it does not exist`
      );
    }
  }

  public destroyAllConnections(): void {
    this.logger.debug(`Destroying all connections`);
    this.logActivePeerConnections("destroyAll(start)");
    this.peerConnections.forEach((_, peerId) => this.destroyConnection(peerId));
    this.pendingPeerConnections.clear();
    this.logActivePeerConnections("destroyAll(end)");
  }

  public async replaceTrackByKind(track: MediaStreamTrack): Promise<void> {
    return Promise.all(
      Array.from(this.peerConnections)
        .map((peerIdPeerConnectionPair) => peerIdPeerConnectionPair[1])
        .map((peerConnection) => peerConnection.replaceTrackByKind(track))
    ).then((_) => undefined);
  }

  // tslint:disable-next-line:readonly-keyword
  private listenForDescriptionSent = async (
    msg: rtcEvents.DescriptionSent
  ): Promise<void> => {
    this.logger.debug(
      `Received RTC description from ${msg.sender}: type=${msg.sdp.type}`
    );

    // For recording calls, the RecordingRTCPool handles signaling, so regular RTCPool should ignore it
    if (this.recordingEnabled) {
      this.logger.debug(
        `Recording is enabled, skipping RTC description handling in regular RTCPool`
      );
      return;
    }

    switch (msg.sdp.type) {
      case "offer":
        return (await this.getRTCPeerConnectionInstance(msg.sender)).handleRemoteOffer(
          msg.sdp
        );
      case "answer":
        return (await this.getRTCPeerConnectionInstance(msg.sender)).handleRemoteAnswer(
          msg.sdp
        );
      default:
        const error = `Received an invalid RTC description type ${msg.sdp.type}`;
        this.logger.error(error);

        return Promise.reject(error);
    }
  };

  // tslint:disable-next-line:readonly-keyword
  private listenForCandidateSent = async (msg: rtcEvents.CandidateSent): Promise<void> => {
    this.logger.debug(
      `Received RTC candidate from ${msg.sender}: ${msg.candidate.candidate}`
    );
    (await this.getRTCPeerConnectionInstance(msg.sender)).addCandidate(msg.candidate);
  };

  private get descriptionSent$(): Observable<rtcEvents.DescriptionSent> {
    return this.getRtcPoolEvent().pipe(filter(rtcEvents.DescriptionSent.is));
  }

  private get candidateSent$(): Observable<rtcEvents.CandidateSent> {
    return this.getRtcPoolEvent().pipe(filter(rtcEvents.CandidateSent.is));
  }

  // tslint:disable-next-line:readonly-keyword
  private getRtcPoolEvent = (): Observable<rtcEvents.RTCSignallingEvent> =>
    this.artichokeApi.domainEvent$
      .pipe(filter(rtcEvents.RTCSignallingEvent.is))
      .pipe(filter((e) => e.callId === this.callId));

  // tslint:disable-next-line:readonly-keyword
  private getRTCPeerConnectionInstance = async (
    peerId: ID
  ): Promise<RTCPeerConnectionFacade> => {
    const maybeRTCPeerConnection = this.peerConnections.get(peerId);
    if (maybeRTCPeerConnection) {
      this.logger.debug(
        `Reusing RTCPeerConnectionFacade for peer ${peerId}`
      );
      this.logActivePeerConnections(`reuse(${peerId})`);

      return maybeRTCPeerConnection;
    } else {
      const pending = this.pendingPeerConnections.get(peerId);
      if (pending) {
        this.logger.debug(
          `Awaiting pending RTCPeerConnectionFacade for peer ${peerId}`
        );

        return pending;
      }

      this.logger.debug(
        `RTCPeerConnectionFacade not exists for peer ${peerId} - creating new`
      );

      const creationPromise = this.createRTCConnectionFacade(peerId)
        .then((rtcConnection) => {
          this.peerConnections.set(peerId, rtcConnection);
          this.pendingPeerConnections.delete(peerId);
          this.logActivePeerConnections(`create(${peerId})`);

          return rtcConnection;
        })
        .catch((err) => {
          this.pendingPeerConnections.delete(peerId);
          throw err;
        });

      this.pendingPeerConnections.set(peerId, creationPromise);

      return creationPromise;
    }
  };

  // tslint:disable-next-line:readonly-keyword
  private getTrackEventHandler =
    (peerId: ID): ((track: MediaStreamTrack) => void) =>
    (track: MediaStreamTrack): void =>
      this.remoteTrackEvent.next({ peerId, track });

  // tslint:disable-next-line:readonly-keyword
  private getConnectionStatusEventHandler =
    (peerId: ID): ((status: ConnectionStatus) => void) =>
    (status: ConnectionStatus): void =>
      this.connectionStatusEvent.next({ peerId, status });

  private async createRTCConnectionFacade(peerId: ID): Promise<RTCPeerConnectionFacade> {
    this.logger.debug(`Creating new RTCConnection for peerId: ${peerId}`);
    const peerCandidateQueueLogger = this.loggerFactory.create(
      `PeerCandidateQueue Peer(${peerId})`
    );

    // Use cached TURN config to avoid multiple API calls
    if (!this.turnConfigPromise) {
      this.logger.debug(`Fetching TURN config for call ${this.callId}`);
      this.turnConfigPromise = this.artichokeApi.getTurnConfig();
    }
    const turnConfig = await this.turnConfigPromise;

    // Create a new config object with TURN servers to avoid mutating shared config
    const peerRtcConfig: RTCConfiguration = {
      ...this.rtcConfig,
      iceServers: [
        {
          urls: turnConfig.urls,
          username: turnConfig.username,
          credential: turnConfig.password
        }
      ]
    };
    const rtcPeerConnection = new RTCPeerConnection(peerRtcConfig);

    return new RTCPeerConnectionFacade(
      this.callId,
      peerId,
      rtcPeerConnection,
      this.artichokeApi,
      this.getTrackEventHandler(peerId),
      this.getConnectionStatusEventHandler(peerId),
      new Queue<RTCIceCandidateInit>(peerCandidateQueueLogger),
      this.loggerFactory.create(
        `RTCPeerConnectionFacade Call(${this.callId}) Peer(${peerId})`
      ),
      new Delayer(),
      this.tracks,
      this.webrtcStats
    );
  }

  private logActivePeerConnections(context: string): void {
    const activePeerIds = Array.from(this.peerConnections.keys());
    this.logger.debug(
      `Peer connection snapshot (${context}): [${activePeerIds.join(", ") || "<none>"}]`
    );
  }
}
